import { Injectable } from '@angular/core';
import { DateTime, Settings } from 'luxon';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';

import * as _ from 'lodash';
import {
  RbacService,
  SystemViewModel,
} from '../../common-api/system-management-api';
import { AppContext } from '../../common/models/app-context';
import { SystemInfo } from '../models/system-info';
import { TimezoneInfo } from '../models/timezone-info';
import { isNullOrEmpty } from '../utils/common-functions';

/**
 * 系統資訊服務
 *
 * @export
 * @class SystemEnvService
 */
@Injectable({
  providedIn: 'root',
})
export class SystemEnvService {
  private sysInfo: SystemInfo = {};
  private sysList: SystemViewModel[] = [];
  timezoneInfo?: TimezoneInfo;

  constructor(
    private rbacService: RbacService,
    private _appContext: AppContext,
    private logger: NGXLogger
  ) {
    this.setupTimezone();
  }

  /**
   * 系統列表
   */
  get systemList(): SystemViewModel[] {
    return this.sysList;
  }

  /**
   * 此系統資訊
   */
  get systemInfo(): SystemInfo {
    return this.sysInfo ?? {};
  }
  /**
   * 設定虛擬系統代號
   * @description for系統功能設定上有虛擬domain(e.g.批覆書作業及供應商管理實際都在merchant這包 )
   * @param systemCode
   */
  setVirtualSystemCode(systemCode: string | undefined) {
    if (systemCode == this.sysInfo.systemVirtaulCode) {
      const useSystemCode = _.cloneDeep(this.sysInfo.systemVirtaulCode);
      this.sysInfo.systemVirtaulCode = this.sysInfo.systemCode;
      this.sysInfo.systemCode = useSystemCode;
      this.logger.info(
        'setVirtualSystemCode',
        useSystemCode,
        this.sysInfo.systemVirtaulCode,
        _.cloneDeep(this.sysInfo)
      );
    }
  }
  systemCode = globalThis.sigv.appContext.projectConfig.systemCode;
  systemVirtualCode =
    globalThis.sigv.appContext.projectConfig.systemVirtualCode;
  /**
   * 初始化系統資訊
   *
   * @return {*}  {Observable<void>}
   * @memberof SystemEnvService
   */
  initSystemInfo(): Observable<void> {
    const useService = this.rbacService;
    const observerable$ = new Observable<void>((observer) => {
      console.log('initSystemInfo', useService.rbacSystemGet()?.subscribe);
      if (useService.rbacSystemGet()?.subscribe) {
        useService.rbacSystemGet()?.subscribe({
          next: (results) => {
            this.logger.info(
              'SystemEnvService - initSystemInfo - rbacSystemGet - success',
              results
            );
            this.sysList = results;
            const currentSystemInfo = results.find(
              (x) => x.systemCode === this.systemCode
            );
            if (isNullOrEmpty(currentSystemInfo)) {
              observer.error(new Error('Fail to get system info.'));
            }
            this.sysInfo = {
              systemId: currentSystemInfo?.systemId,
              systemCode: currentSystemInfo?.systemCode,
              systemName: currentSystemInfo?.systemName,
              systemHostUrl: currentSystemInfo?.systemHostUrl,
              systemVirtaulCode: this.systemVirtualCode,
            };

            observer.next();
            observer.complete();
          },
          error: (err) => {
            observer.error(err);
          },
        });
      }
    });
    return observerable$;
  }
  mockInitSystemInfo(): Observable<void> {
    const observerable$ = new Observable<void>((observer) => {
      this.sysList = [
        {
          systemId: 28,
          systemCode: 'RetailCreditRatingScales',
          systemName: 'Retail Credit Review',
          systemHostUrl: '',
          systemGroup: 'rtCore',
          systemNameEnglish: 'Retail Credit Review',
          systemNameLocal: '消金審查系統',
          sequence: 1,
          systemFunctions: [],
        },
        {
          systemId: 28,
          systemCode: 'RetailWatchList',
          systemName: 'Retail Watch List',
          systemHostUrl: '',
          systemGroup: 'rtCore',
          systemNameEnglish: 'Retail Watch List',
          systemNameLocal: '消金觀察者清單系統',
          sequence: 1,
          systemFunctions: [],
        },
        {
          systemId: 28,
          systemCode: 'RetailCustormerService',
          systemName: 'Retail Custormer Service',
          systemHostUrl: '',
          systemGroup: 'rtCore',
          systemNameEnglish: 'Retail Custormer Service',
          systemNameLocal: '消金客服系統',
          sequence: 1,
          systemFunctions: [],
        },
      ];
      const currentSystemInfo = this.sysList.find(
        (x) => x.systemCode === this.systemCode
      );
      if (isNullOrEmpty(currentSystemInfo)) {
        observer.error(new Error('Fail to get system info.'));
      }
      this.sysInfo = {
        systemId: currentSystemInfo?.systemId,
        systemCode: currentSystemInfo?.systemCode,
        systemName: currentSystemInfo?.systemName,
        systemHostUrl: currentSystemInfo?.systemHostUrl,
      };
      this.logger.info(
        'SystemEnvService -Mock initSystemInfo - rbacSystemGet - success',
        this.sysList,
        this.sysInfo
      );
      observer.next();
      observer.complete();
    });
    return observerable$;
  }

  /**
   * 設定時區
   *
   * @private
   * @memberof SystemEnvService
   */
  private setupTimezone(): void {
    if (isNullOrEmpty(this._appContext.envConfig?.timezone) === false) {
      Settings.defaultZone = this._appContext.envConfig.timezone;
    }
    const local = DateTime.local();
    this.timezoneInfo = {
      zoneName: local.zoneName,
      offset: local.offset,
      offsetName: TimezoneInfo.getUTCOffsetString(local.offset),
    };
  }
}
